import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { LockProvider, useLock } from './context/LockContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import LockScreen from './components/LockScreen';
import ErrorBoundary from './components/ErrorBoundary';
import ShortcutHelpModal from './components/ShortcutHelpModal';
import CommandPalette from './components/CommandPalette';
import useGlobalShortcuts from './hooks/useGlobalShortcuts';
import { useLanguage as useLanguageForA11y } from './context/LanguageContext';
import Sidebar from './components/Layout/Sidebar';
import Header, { NotificationBell } from './components/Layout/Header';
import SuperAdminSidebar from './components/Layout/SuperAdminSidebar';
import ChainAdminSidebar from './components/Layout/ChainAdminSidebar';
import OnboardingTour from './components/OnboardingTour';
import DocumentTitle from './components/DocumentTitle';
import OfflineBanner from './components/OfflineBanner';

// Eager (first-paint critical — login, default landings, public customer screens)
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tables from './pages/Tables';
import Orders from './pages/Orders';
import Kitchen from './pages/Kitchen';
import QRMenu from './pages/QRMenu';
import POSDisplay from './pages/POSDisplay';

// Lazy (admin / infrequent / public-but-secondary)
const MenuManagement = lazy(() => import('./pages/MenuManagement'));
const Stock          = lazy(() => import('./pages/Stock'));
const Recipes        = lazy(() => import('./pages/Recipes'));
const Suppliers      = lazy(() => import('./pages/Suppliers'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const CashTransactions = lazy(() => import('./pages/CashTransactions'));
const Refunds       = lazy(() => import('./pages/Refunds'));
const StockAdjustments = lazy(() => import('./pages/StockAdjustments'));
const Payments       = lazy(() => import('./pages/Payments'));
const Users          = lazy(() => import('./pages/Users'));
const Pricing        = lazy(() => import('./pages/Pricing'));
const Checkout       = lazy(() => import('./pages/Checkout'));
const PaymentResult  = lazy(() => import('./pages/PaymentResult'));
const MySubscription = lazy(() => import('./pages/MySubscription'));
const WaiterStats    = lazy(() => import('./pages/WaiterStats'));
const Reports        = lazy(() => import('./pages/Reports'));
const Settings       = lazy(() => import('./pages/Settings'));
const SuperDashboard     = lazy(() => import('./pages/superadmin/SuperDashboard'));
const Restaurants        = lazy(() => import('./pages/superadmin/Restaurants'));
const RestaurantDetail   = lazy(() => import('./pages/superadmin/RestaurantDetail'));
const SaasPlans          = lazy(() => import('./pages/superadmin/SaasPlans'));
const SaasSubscriptions  = lazy(() => import('./pages/superadmin/SaasSubscriptions'));
const ChainDashboard = lazy(() => import('./pages/chain/ChainDashboard'));
const ChainReports   = lazy(() => import('./pages/chain/ChainReports'));
const ChainBranches  = lazy(() => import('./pages/chain/ChainBranches'));
const ChainMenu      = lazy(() => import('./pages/chain/ChainMenu'));
const ChainStaff     = lazy(() => import('./pages/chain/ChainStaff'));
const PromoCodes        = lazy(() => import('./pages/PromoCodes'));
const GiftVouchers      = lazy(() => import('./pages/GiftVouchers'));
const Customers         = lazy(() => import('./pages/Customers'));
const DeliveryOrders    = lazy(() => import('./pages/DeliveryOrders'));
const Couriers          = lazy(() => import('./pages/Couriers'));
const PlatformManagement= lazy(() => import('./pages/PlatformManagement'));
const POSTerminal       = lazy(() => import('./pages/POSTerminal'));
const PosProducts       = lazy(() => import('./pages/pos/PosProducts'));
const PosReports        = lazy(() => import('./pages/pos/PosReports'));
const PosReceiptSettings = lazy(() => import('./pages/pos/PosReceiptSettings'));
const PrinterManagement = lazy(() => import('./pages/PrinterManagement'));
const ForgotPassword    = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword     = lazy(() => import('./pages/ResetPassword'));
const Reservations      = lazy(() => import('./pages/Reservations'));
const Shifts            = lazy(() => import('./pages/Shifts'));
const GDPR              = lazy(() => import('./pages/GDPR'));
const WhiteLabel        = lazy(() => import('./pages/WhiteLabel'));
const Webhooks          = lazy(() => import('./pages/Webhooks'));
const AuditLogs         = lazy(() => import('./pages/AuditLogs'));
const Security          = lazy(() => import('./pages/Security'));
const Trash             = lazy(() => import('./pages/Trash'));
const TaxGroups         = lazy(() => import('./pages/TaxGroups'));
const FeatureFlags      = lazy(() => import('./pages/FeatureFlags'));
const Notifications     = lazy(() => import('./pages/Notifications'));
const CustomerDetail    = lazy(() => import('./pages/CustomerDetail'));
const Onboarding        = lazy(() => import('./pages/Onboarding'));
const HR               = lazy(() => import('./pages/HR'));
const NotFound          = lazy(() => import('./pages/NotFound'));
const OrderTrack        = lazy(() => import('./pages/OrderTrack'));
const ServerError       = lazy(() => import('./pages/ServerError'));
const Forbidden         = lazy(() => import('./pages/Forbidden'));

function LoadingScreen() {
  const { t } = useLanguageForA11y();
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="text-4xl mb-3" aria-hidden="true">🍴</div>
        <div className="text-gray-500 dark:text-gray-400">{t('loading') || 'Yükleniyor...'}</div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly = false, allowCashier = false }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  // Cashier kilidi: sadece allowCashier=true olan route'lara erişebilir, diğerlerinde /pos'a redirect.
  if (user.role === 'cashier' && !allowCashier) return <Navigate to="/pos" replace />;
  if (adminOnly && user.role !== 'admin' && user.role !== 'superadmin') return <Navigate to="/forbidden" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'superadmin') return <Navigate to="/" replace />;
  return children;
}

function ChainAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'chain_admin' && user.role !== 'superadmin') return <Navigate to="/" replace />;
  return children;
}

function Layout({ children }) {
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Header />
        <main id="main-content" className="flex-1 overflow-y-auto app-scroll p-4 md:p-6 pb-20 md:pb-6">
          {children}
        </main>
      </div>
    </div>
  );
}

function SuperAdminLayout({ children }) {
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <SuperAdminSidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="bg-indigo-900 dark:bg-gray-900 border-b border-indigo-700 dark:border-gray-700 px-4 md:px-6 py-3 flex items-center justify-between">
          <h1 className="text-sm font-semibold text-indigo-100 ml-10 md:ml-0">SuperAdmin Paneli</h1>
        </div>
        <main id="main-content" className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

function ChainAdminLayout({ children }) {
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <ChainAdminSidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <div className="bg-emerald-900 dark:bg-gray-900 border-b border-emerald-700 dark:border-gray-700 px-4 md:px-6 py-3 flex items-center justify-between">
          <h1 className="text-sm font-semibold text-emerald-100 ml-10 md:ml-0">Zincir Yönetim Paneli</h1>
        </div>
        <main id="main-content" className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

/* ─── Waiter Lock Settings Panel ───────────────────────────────── */
/* ─── Touch PIN Pad (klavyesiz) ─────────────────────────────────── */
function TouchPinPad({ value, onChange, onClear }) {
  const press = (d) => { if (value.length < 4) onChange(value + d); };
  const back  = () => onChange(value.slice(0, -1));
  return (
    <div className="flex flex-col items-center gap-3 select-none">
      {/* dots */}
      <div className="flex gap-4 mb-1">
        {[0,1,2,3].map(i => (
          <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all ${
            value.length > i ? 'bg-blue-500 border-blue-400 scale-110' : 'border-gray-300 dark:border-gray-600'
          }`} />
        ))}
      </div>
      {/* grid */}
      <div className="grid grid-cols-3 gap-2 w-full max-w-[260px]">
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} onClick={() => press(String(n))}
            className="h-14 rounded-xl bg-gray-100 dark:bg-gray-800 active:bg-blue-100 dark:active:bg-blue-900/40 text-gray-800 dark:text-white text-xl font-semibold transition-all active:scale-90 flex items-center justify-center border border-gray-200 dark:border-gray-700">
            {n}
          </button>
        ))}
        <button onClick={onClear}
          className="h-14 rounded-xl bg-gray-50 dark:bg-gray-900 active:bg-red-50 dark:active:bg-red-900/20 text-gray-400 dark:text-gray-500 text-xs font-semibold transition-all active:scale-90 flex items-center justify-center border border-gray-200 dark:border-gray-700">
          Temizle
        </button>
        <button onClick={() => press('0')}
          className="h-14 rounded-xl bg-gray-100 dark:bg-gray-800 active:bg-blue-100 dark:active:bg-blue-900/40 text-gray-800 dark:text-white text-xl font-semibold transition-all active:scale-90 flex items-center justify-center border border-gray-200 dark:border-gray-700">
          0
        </button>
        <button onClick={back}
          className="h-14 rounded-xl bg-gray-100 dark:bg-gray-800 active:bg-gray-200 dark:active:bg-gray-700 text-gray-600 dark:text-gray-300 text-xl transition-all active:scale-90 flex items-center justify-center border border-gray-200 dark:border-gray-700">
          ⌫
        </button>
      </div>
    </div>
  );
}

function WaiterLockSettings({ onClose }) {
  const { user } = useAuth();
  const { settings, updateSettings, registerPin, removePin, currentUserPin, pinSessions } = useLock();
  // step: 'menu' | 'set-new' | 'confirm'
  const [step, setStep] = useState('menu');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');

  const goToSetPin = () => { setStep('set-new'); setNewPin(''); setConfirmPin(''); setPinError(''); };
  const goToConfirm = () => {
    const conflict = pinSessions.find(s => s.pin === newPin && s.userId !== user.id);
    if (conflict) { setPinError('Bu PIN başka bir kullanıcı tarafından kullanılıyor'); return; }
    setPinError('');
    setStep('confirm');
    setConfirmPin('');
  };
  const handleSavePin = () => {
    if (newPin !== confirmPin) { setPinError('PIN kodları eşleşmiyor'); setConfirmPin(''); return; }
    registerPin(newPin);
    setStep('menu');
    setPinSuccess('PIN kodu kaydedildi!');
    setTimeout(() => setPinSuccess(''), 2500);
  };

  const timeoutOptions = [
    { label: '15 sn', value: 15 },
    { label: '30 sn', value: 30 },
    { label: '1 dk', value: 60 },
    { label: '2 dk', value: 120 },
    { label: '5 dk', value: 300 },
  ];

  const title = step === 'set-new' ? (currentUserPin ? 'Yeni PIN Girin' : 'PIN Belirle')
              : step === 'confirm' ? 'PIN Tekrar Girin'
              : '⚙️ Kilit Ayarları';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-t-3xl md:rounded-2xl shadow-2xl w-full md:max-w-md max-h-[90vh] flex flex-col">
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            {step !== 'menu' && (
              <button onClick={() => { setStep('menu'); setPinError(''); }}
                className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 text-sm active:scale-95">←</button>
            )}
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          </div>
          <button onClick={onClose} aria-label="close" className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 active:scale-95">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">

          {/* ── Step: set-new ── */}
          {step === 'set-new' && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">4 haneli yeni PIN'inizi girin</p>
              <TouchPinPad value={newPin} onChange={setNewPin} onClear={() => setNewPin('')} />
              {pinError && <p className="text-red-500 text-xs font-medium">{pinError}</p>}
              <button onClick={goToConfirm} disabled={newPin.length !== 4}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-semibold disabled:opacity-40 active:bg-blue-800 text-base mt-2">
                Devam →
              </button>
            </div>
          )}

          {/* ── Step: confirm ── */}
          {step === 'confirm' && (
            <div className="flex flex-col items-center gap-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">PIN'i tekrar girin</p>
              <TouchPinPad value={confirmPin} onChange={v => { setConfirmPin(v); setPinError(''); }} onClear={() => setConfirmPin('')} />
              {pinError && <p className="text-red-500 text-xs font-medium text-center">{pinError}</p>}
              <button onClick={handleSavePin} disabled={confirmPin.length !== 4}
                className="w-full py-4 bg-green-600 text-white rounded-2xl font-semibold disabled:opacity-40 active:bg-green-800 text-base mt-2">
                ✓ PIN Kaydet
              </button>
            </div>
          )}

          {/* ── Step: menu ── */}
          {step === 'menu' && (
            <div className="space-y-5">
              {pinSuccess && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-xl text-sm font-medium">
                  ✓ {pinSuccess}
                </div>
              )}

              {/* Auto-lock toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800 dark:text-white text-sm">Otomatik Kilit</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Hareketsizlikte ekranı kilitle</p>
                </div>
                <button onClick={() => updateSettings({ enabled: !settings.enabled })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${settings.enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${settings.enabled ? 'left-6' : 'left-0.5'}`} />
                </button>
              </div>

              {/* Timeout */}
              {settings.enabled && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Kilit Süresi</p>
                  <div className="flex flex-wrap gap-2">
                    {timeoutOptions.map(opt => (
                      <button key={opt.value} onClick={() => updateSettings({ timeoutSeconds: opt.value })}
                        className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${settings.timeoutSeconds === opt.value ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* PIN actions */}
              <div className="space-y-2 pt-1">
                <button onClick={goToSetPin}
                  className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold active:bg-blue-800 transition-colors">
                  {currentUserPin ? '🔑 PIN Değiştir' : '🔑 PIN Belirle'}
                </button>
                {currentUserPin && (
                  <button onClick={() => { removePin(user.id); setPinSuccess('PIN kaldırıldı.'); }}
                    className="w-full py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-medium text-sm border border-red-200 dark:border-red-800 active:bg-red-100">
                    PIN Kaldır
                  </button>
                )}
              </div>

              {/* Other registered sessions */}
              {pinSessions.filter(s => s.userId !== user?.id).length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Kayıtlı Diğer Kullanıcılar</p>
                  <div className="space-y-2">
                    {pinSessions.filter(s => s.userId !== user?.id).map(s => (
                      <div key={s.userId} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-600 dark:text-gray-300">
                            {s.userName?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{s.userName}</span>
                        </div>
                        <button onClick={() => removePin(s.userId)} className="text-xs text-red-500 font-semibold">Kaldır</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WaiterLayout({ children }) {
  const { user, logout } = useAuth();
  const { lock } = useLock();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSettings, setShowSettings] = useState(false);

  const tabs = [
    { to: '/tables', icon: '🍽️', label: 'Masalar' },
    { to: '/orders', icon: '📋', label: 'Siparişler' },
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-lg">🍽️</div>
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500 leading-none">Garson</p>
            <p className="font-bold text-gray-900 dark:text-white text-sm leading-tight">{user?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* UI-04 parça 6/6: Waiter tabbar top-bar bildirim rozeti */}
          <NotificationBell />
          <button
            onClick={lock}
            title="Ekranı Kilitle"
            className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 active:bg-gray-200 dark:active:bg-gray-600 transition-colors text-base">
            🔒
          </button>
          <button
            onClick={() => setShowSettings(true)}
            title="Kilit Ayarları"
            className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 active:bg-gray-200 dark:active:bg-gray-600 transition-colors text-base">
            ⚙️
          </button>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="text-xs font-semibold text-red-500 dark:text-red-400 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 active:bg-red-50 dark:active:bg-red-900/20 transition-colors">
            Çıkış
          </button>
        </div>
      </div>

      {/* Main content */}
      <main id="main-content" className="flex-1 overflow-y-auto app-scroll p-4 pb-6">
        {children}
      </main>

      {/* Bottom tabbar */}
      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex flex-shrink-0 pb-safe">
        {tabs.map(tab => {
          const isActive = location.pathname === tab.to;
          return (
            <button
              key={tab.to}
              onClick={() => navigate(tab.to)}
              className={`flex-1 flex flex-col items-center py-3 gap-0.5 transition-colors active:bg-gray-50 dark:active:bg-gray-700/50 ${
                isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
              }`}>
              <span className="text-2xl leading-none">{tab.icon}</span>
              <span className={`text-xs font-semibold mt-0.5 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
                {tab.label}
              </span>
              {isActive && <div className="w-5 h-0.5 rounded-full bg-blue-600 dark:bg-blue-400 mt-0.5" />}
            </button>
          );
        })}
      </div>

      {showSettings && <WaiterLockSettings onClose={() => setShowSettings(false)} />}
    </div>
  );
}

function SmartLayout({ children }) {
  const { user } = useAuth();
  if (user?.role === 'waiter') return <WaiterLayout>{children}</WaiterLayout>;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={user ? <Navigate to={user.role === 'superadmin' ? '/superadmin' : user.role === 'chain_admin' ? '/chain' : user.role === 'kitchen' ? '/kitchen' : user.role === 'waiter' ? '/tables' : user.role === 'cashier' ? '/pos' : '/'} replace /> : <Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/menu/:tableId" element={<QRMenu />} />
      <Route path="/track/:token" element={<OrderTrack />} />
      <Route path="/pricing" element={<Pricing />} />

      {/* SuperAdmin routes */}
      <Route path="/superadmin" element={<SuperAdminRoute><SuperAdminLayout><SuperDashboard /></SuperAdminLayout></SuperAdminRoute>} />
      <Route path="/superadmin/restaurants" element={<SuperAdminRoute><SuperAdminLayout><Restaurants /></SuperAdminLayout></SuperAdminRoute>} />
      <Route path="/superadmin/restaurants/:id" element={<SuperAdminRoute><SuperAdminLayout><RestaurantDetail /></SuperAdminLayout></SuperAdminRoute>} />
      <Route path="/superadmin/plans" element={<SuperAdminRoute><SuperAdminLayout><SaasPlans /></SuperAdminLayout></SuperAdminRoute>} />
      <Route path="/superadmin/subscriptions" element={<SuperAdminRoute><SuperAdminLayout><SaasSubscriptions /></SuperAdminLayout></SuperAdminRoute>} />

      {/* Chain Admin routes */}
      <Route path="/chain" element={<ChainAdminRoute><ChainAdminLayout><ChainDashboard /></ChainAdminLayout></ChainAdminRoute>} />
      <Route path="/chain/branches" element={<ChainAdminRoute><ChainAdminLayout><ChainBranches /></ChainAdminLayout></ChainAdminRoute>} />
      <Route path="/chain/reports" element={<ChainAdminRoute><ChainAdminLayout><ChainReports /></ChainAdminLayout></ChainAdminRoute>} />
      <Route path="/chain/analytics" element={<ChainAdminRoute><ChainAdminLayout><ChainReports /></ChainAdminLayout></ChainAdminRoute>} />
      <Route path="/chain/menu" element={<ChainAdminRoute><ChainAdminLayout><ChainMenu /></ChainAdminLayout></ChainAdminRoute>} />
      <Route path="/chain/staff" element={<ChainAdminRoute><ChainAdminLayout><ChainStaff /></ChainAdminLayout></ChainAdminRoute>} />

      {/* Kitchen (separate fullscreen) */}
      <Route path="/kitchen" element={<ProtectedRoute><Kitchen /></ProtectedRoute>} />

      {/* POS Müşteri Ekranı (public — auth gerekmez, ikinci monitörde açılır) */}
      <Route path="/pos-display" element={<POSDisplay />} />

      {/* Standalone pages (no Layout) */}
      <Route path="/checkout" element={<ProtectedRoute adminOnly><Checkout /></ProtectedRoute>} />
      <Route path="/subscription/result" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />

      {/* Restaurant routes */}
      <Route path="/" element={<ProtectedRoute>{user?.role === 'waiter' ? <Navigate to="/tables" replace /> : <Layout><Dashboard /></Layout>}</ProtectedRoute>} />
      <Route path="/tables" element={<ProtectedRoute><SmartLayout><Tables /></SmartLayout></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><SmartLayout><Orders /></SmartLayout></ProtectedRoute>} />
      <Route path="/menu-management" element={<ProtectedRoute adminOnly><Layout><MenuManagement /></Layout></ProtectedRoute>} />
      <Route path="/stock" element={<ProtectedRoute adminOnly><Layout><Stock /></Layout></ProtectedRoute>} />
      <Route path="/recipes" element={<ProtectedRoute adminOnly><Layout><Recipes /></Layout></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute adminOnly><Layout><Suppliers /></Layout></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute adminOnly><Layout><PurchaseOrders /></Layout></ProtectedRoute>} />
      <Route path="/cash-transactions" element={<ProtectedRoute adminOnly><Layout><CashTransactions /></Layout></ProtectedRoute>} />
      <Route path="/refunds" element={<ProtectedRoute adminOnly><Layout><Refunds /></Layout></ProtectedRoute>} />
      <Route path="/stock-history" element={<ProtectedRoute adminOnly><Layout><StockAdjustments /></Layout></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute adminOnly><Layout><Payments /></Layout></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute adminOnly><Layout><Users /></Layout></ProtectedRoute>} />
      <Route path="/my-subscription" element={<ProtectedRoute adminOnly><Layout><MySubscription /></Layout></ProtectedRoute>} />
      <Route path="/waiter-stats" element={<ProtectedRoute adminOnly><Layout><WaiterStats /></Layout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute adminOnly><Layout><Reports /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Layout><Settings /></Layout></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Layout><Customers /></Layout></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><Layout><CustomerDetail /></Layout></ProtectedRoute>} />
      <Route path="/promo-codes" element={<ProtectedRoute adminOnly><Layout><PromoCodes /></Layout></ProtectedRoute>} />
      <Route path="/gift-vouchers" element={<ProtectedRoute adminOnly><Layout><GiftVouchers /></Layout></ProtectedRoute>} />
      <Route path="/delivery-orders" element={<ProtectedRoute adminOnly><Layout><DeliveryOrders /></Layout></ProtectedRoute>} />
      <Route path="/couriers" element={<ProtectedRoute adminOnly><Layout><Couriers /></Layout></ProtectedRoute>} />
      <Route path="/platform-management" element={<ProtectedRoute adminOnly><Layout><PlatformManagement /></Layout></ProtectedRoute>} />
      <Route path="/pos" element={<ProtectedRoute allowCashier><POSTerminal /></ProtectedRoute>} />
      <Route path="/pos-products" element={<ProtectedRoute adminOnly><Layout><PosProducts /></Layout></ProtectedRoute>} />
      <Route path="/pos-reports" element={<ProtectedRoute adminOnly><Layout><PosReports /></Layout></ProtectedRoute>} />
      <Route path="/pos-receipt-settings" element={<ProtectedRoute adminOnly><Layout><PosReceiptSettings /></Layout></ProtectedRoute>} />
      <Route path="/printers" element={<ProtectedRoute adminOnly><Layout><PrinterManagement /></Layout></ProtectedRoute>} />
      <Route path="/reservations" element={<ProtectedRoute adminOnly><Layout><Reservations /></Layout></ProtectedRoute>} />
      <Route path="/shifts" element={<ProtectedRoute adminOnly><Layout><Shifts /></Layout></ProtectedRoute>} />
      <Route path="/gdpr" element={<ProtectedRoute><Layout><GDPR /></Layout></ProtectedRoute>} />
      <Route path="/white-label" element={<SuperAdminRoute><Layout><WhiteLabel /></Layout></SuperAdminRoute>} />
      <Route path="/webhooks" element={<ProtectedRoute><Layout><Webhooks /></Layout></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute adminOnly><Layout><AuditLogs /></Layout></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><Layout><Security /></Layout></ProtectedRoute>} />
      <Route path="/trash" element={<ProtectedRoute adminOnly><Layout><Trash /></Layout></ProtectedRoute>} />
      <Route path="/tax-groups" element={<ProtectedRoute adminOnly><Layout><TaxGroups /></Layout></ProtectedRoute>} />
      <Route path="/feature-flags" element={<ProtectedRoute adminOnly><Layout><FeatureFlags /></Layout></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Layout><Notifications /></Layout></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute adminOnly><Onboarding /></ProtectedRoute>} />
      <Route path="/hr" element={<ProtectedRoute adminOnly><Layout><HR /></Layout></ProtectedRoute>} />

      <Route path="/server-error" element={<ServerError />} />
      <Route path="/forbidden" element={<Forbidden />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function LockScreenWrapper() {
  const { isLocked } = useLock();
  if (!isLocked) return null;
  return <LockScreen />;
}

function GlobalShortcutsWrapper() {
  const { helpOpen, closeHelp, paletteOpen, closePalette } = useGlobalShortcuts();
  return (
    <>
      <ShortcutHelpModal open={helpOpen} onClose={closeHelp} />
      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </>
  );
}

/* A11y: Skip-to-content link — Tab basıldığında ilk fokuslanan eleman.
 * Klavye ve screen reader kullanıcıları sidebar/header'ı atlayıp doğrudan
 * ana içeriğe gidebilir. */
function SkipToContent() {
  const { t } = useLanguageForA11y();
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
    >
      {t('skipToContent') || t('skipToMain') || 'Ana içeriğe atla'}
    </a>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <SubscriptionProvider>
              <LockProvider>
                <ToastProvider>
                  <ConfirmProvider>
                    <SkipToContent />
                    <OfflineBanner />
                    <LockScreenWrapper />
                    <GlobalShortcutsWrapper />
                    <DocumentTitle />
                    <OnboardingTour />
                    <ErrorBoundary>
                      <Suspense fallback={<LoadingScreen />}>
                        <AppRoutes />
                      </Suspense>
                    </ErrorBoundary>
                  </ConfirmProvider>
                </ToastProvider>
              </LockProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
